import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, ChevronLeft, ChevronRight, RefreshCw, FileSpreadsheet, Plus, CheckCircle, Clock, FileText, Download } from 'lucide-react';
import Breadcrumb, { BREADCRUMB_ACTION_BTN, BREADCRUMB_EXPORT_BTN } from '../../../components/Breadcrumb/Breadcrumb';
import Table, { TABLE_THEME } from '../../../components/Table/Table';
import SearchableSelect from '../../../components/common/SearchableSelect';
import {
  fetchBankReportsByDate,
  clearPayrollTextError,
  selectBankReports,
  selectPayrollTextLoading,
  selectPayrollTextError,
  selectPayrollTextSelectedDate,
  fetchCurrentPeriodId,
  selectCurrentPeriodId,
  markBankReportStatus,
  selectValidationState,
  clearStageError,
  resetValidationState,
} from '../../../store/slices/payrollSlice';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const FIELD = `${TABLE_THEME.fieldBase} ${TABLE_THEME.fieldFocus}`;
const LABEL = TABLE_THEME.fieldLabel;

/*
|--------------------------------------------------------------------------
| Month-Year Picker Component
|--------------------------------------------------------------------------
*/
function MonthYearPicker({ value, onChange, className = '' }) {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const [selectedYear, setSelectedYear] = useState(() => {
    if (value) {
      const parts = value.split('-');
      return parseInt(parts[0]) || currentYear;
    }
    return currentYear;
  });

  const [selectedMonth, setSelectedMonth] = useState(() => {
    if (value) {
      const parts = value.split('-');
      return parseInt(parts[1]) || (currentMonth + 1);
    }
    return currentMonth + 1;
  });

  const yearOptions = [];
  for (let i = 0; i < 25; i++) {
    const year = currentYear - i;
    yearOptions.push(year);
  }

  useEffect(() => {
    const formattedValue = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
    onChange(formattedValue);
  }, [selectedYear, selectedMonth, onChange]);

  return (
    <div className={`flex items-end gap-2 ${className}`}>
      <div className="w-28">
        <span className={LABEL}>Year</span>
        <SearchableSelect
          value={selectedYear}
          onChange={(v) => setSelectedYear(parseInt(v, 10))}
          options={yearOptions.map((year) => ({ value: year, label: String(year) }))}
          searchPlaceholder="Search year..."
          className={`${FIELD} w-full`}
        />
      </div>

      <div className="w-36">
        <span className={LABEL}>Month</span>
        <SearchableSelect
          value={selectedMonth}
          onChange={(v) => setSelectedMonth(parseInt(v, 10))}
          options={months.map((month, index) => ({ value: index + 1, label: month }))}
          searchPlaceholder="Search month..."
          className={`${FIELD} w-full`}
        />
      </div>
    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Main Component
|--------------------------------------------------------------------------
*/
function PayrollText() {
  const dispatch = useDispatch();

  const bankReports = useSelector(selectBankReports);
  const loading = useSelector(selectPayrollTextLoading);
  const error = useSelector(selectPayrollTextError);
  const selectedDate = useSelector(selectPayrollTextSelectedDate);
  const currentPeriodId = useSelector(selectCurrentPeriodId);

  const [search, setSearch] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [isApproved, setIsApproved] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateSuccess, setGenerateSuccess] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const ROWS_PER_PAGE = 10;

  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [isPeriodLoaded, setIsPeriodLoaded] = useState(false);

  const validation = useSelector(selectValidationState);

  // ─── Fetch current period ID on mount ──────────────────────────────────
  useEffect(() => {
    dispatch(fetchCurrentPeriodId());
  }, [dispatch]);

  // ─── Set selectedPeriodId when currentPeriodId is available ──────────
  useEffect(() => {
    if (currentPeriodId && !selectedPeriodId) {
      console.log('[PayrollText] Setting selectedPeriodId to:', currentPeriodId);
      setSelectedPeriodId(currentPeriodId);
      setIsPeriodLoaded(true);
    }
  }, [currentPeriodId, selectedPeriodId]);


  // ─── Fetch data when monthFilter changes ──────────────────────────────
  useEffect(() => {
    if (monthFilter && monthFilter.includes('-')) {
      const [year, month] = monthFilter.split('-');
      const formattedDate = `${year}-${month}`;
      dispatch(fetchBankReportsByDate(formattedDate));
    }
  }, [monthFilter, dispatch]);

  // Reset approval state when month changes
  useEffect(() => {
    setIsApproved(false);
    setGenerateSuccess(false);
    setGenerateError('');
    // Reset validation when the month changes
    dispatch(resetValidationState());
  }, [monthFilter, dispatch]);

  // ─── Handle Approve / Validate ────────────────────────────────────────
  const handleValidate = () => {
    if (!hasFilter) return;
    if (filteredReports.length === 0) return;

    const [year, month] = monthFilter.split('-');

    dispatch(
      markBankReportStatus({
        year,
        month,
        stage: activeStage,   // 'prepared' | 'authorized' | 'approved'
      })
    );
  };

  // ─── Handle Export Excel ──────────────────────────────────────────────────
  const handleExportExcel = () => {
    if (!filteredReports || filteredReports.length === 0) {
      alert('No data to export.');
      return;
    }

    // Get current date and time for the heading
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    const timeStr = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // Get month display
    let monthDisplay = monthFilter || '';
    if (!monthDisplay) {
      const currentMonth = now.toLocaleString('en-US', { month: 'short' });
      const currentYear = now.getFullYear();
      monthDisplay = `${currentMonth}-${currentYear}`;
    } else {
      const [year, month] = monthDisplay.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      monthDisplay = `${monthNames[parseInt(month) - 1] || month}-${year}`;
    }

    // Define column headers
    const headers = [
      'Transaction ID',
      'Bank Name',
      'Destination Bank',
      'Branch Name',
      'Destination Branch',
      'Branch Swift Code',
      'Year',
      'Month',
      'Destination Account',
      'Destination Account Name',
      'Transaction Reference Number',
      'Return Code',
      'Credit/Debit Code',
      'Return Date',
      'Amount',
      'Currency Code',
      'Originating Bank',
      'Originating Branch',
      'Originating Account',
      'Originating Account Name',
      'Particulars',
      'Reference',
      'Value Date',
      'Security Field',
      'Filler',
      'NIC Number'
    ];

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Payroll Text Reports');

    // --- HEADING (Row 1) ---
    const headingText = `Bank CEFT Report Month ${monthDisplay} | Time ${dateStr} ${timeStr}`;
    const headingRow = worksheet.addRow([headingText]);
    const lastColumnLetter = String.fromCharCode(64 + headers.length);
    worksheet.mergeCells(`A1:${lastColumnLetter}1`);

    headingRow.getCell(1).alignment = {
      horizontal: 'center',
      vertical: 'center'
    };
    headingRow.getCell(1).font = {
      bold: true,
      size: 14
    };
    headingRow.height = 30;

    // --- EMPTY ROW (Row 2)
    worksheet.addRow([]);

    // --- HEADERS (Row 3)
    const headerRow = worksheet.addRow(headers);
    headerRow.height = 25;

    headerRow.eachCell((cell) => {
      cell.alignment = {
        horizontal: 'center',
        vertical: 'center'
      };
      cell.font = {
        bold: true,
        size: 11
      };
      // ADD BORDERS
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // --- DATA ROWS
    filteredReports.forEach((report) => {
      const dataRow = worksheet.addRow([
        report.HBR_Tran_ID || report.id || report.tranId || '0',
        report.HBR_Bank_Name || report.bankName || '',
        report.HBR_Destination_Bank || report.destinationBank || '',
        report.HBR_Branch_Name || report.branchName || '',
        report.HBR_Destination_Br || report.destinationBr || '',
        report.HBR_Branch_Swift_Code || report.branchSwiftCode || '',
        report.HBR_Year || report.year || '',
        (() => {
          const m = report.HBR_Month ?? report.month;
          if (!m) return '';
          const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return names[Number(m) - 1] || String(m);
        })(),
        report.HBR_Destination_Account || report.destinationAccount || '',
        report.HBR_Destination_Account_Name || report.destinationAccountName || '',
        report.HBR_TRN_Code || report.trnCode || '0',
        report.HBR_Return_Code || report.returnCode || '0',
        report.HBR_Cr_Dr_Code || report.crDrCode || '0',
        report.HBR_Return_Date || report.returnDate || '',
        parseFloat(report.HBR_Amount || report.amount || 0),
        report.HBR_Currency_Code || report.currencyCode || 'SLR',
        report.HBR_Originating_Bank || report.originatingBank || '',
        report.HBR_Originating_Branch || report.originatingBranch || '',
        report.HBR_Originating_Account || report.originatingAccount || '',
        report.HBR_Originating_Account_Name || report.originatingAccountName || '',
        report.HBR_Particulars || report.particulars || '',
        report.HBR_Reference || report.reference || '',
        report.HBR_Value_Date || report.valueDate || '0',
        report.HBR_Security_Field || report.securityField || '0',
        report.HBR_Filler || report.filler || '@',
        report.HBR_NIC_Number || report.nicNumber || ''
      ]);

      dataRow.eachCell((cell) => {
        cell.alignment = {
          horizontal: 'center',
          vertical: 'center'
        };
        // ADD BORDERS (grid lines)
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });

    // --- TOTAL ROW
    const totalAmount = filteredReports.reduce((sum, report) => {
      return sum + (parseFloat(report.HBR_Amount || report.amount || 0));
    }, 0);

    const totalRowData = new Array(headers.length).fill('');
    totalRowData[0] = 'TOTAL';
    totalRowData[14] = totalAmount;

    const totalRow = worksheet.addRow(totalRowData);
    totalRow.height = 25;

    totalRow.eachCell((cell) => {
      cell.alignment = {
        horizontal: 'center',
        vertical: 'center'
      };
      cell.font = {
        bold: true
      };
      // ADD BORDERS
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // --- COLUMN WIDTHS ---
    worksheet.columns = [
      { width: 15 }, // Transaction ID
      { width: 20 }, // Bank Name
      { width: 20 }, // Destination Bank
      { width: 20 }, // Branch Name
      { width: 20 }, // Destination Branch
      { width: 15 }, // Branch Swift Code
      { width: 10 }, // Year         
      { width: 10 }, // Month  
      { width: 20 }, // Destination Account
      { width: 25 }, // Destination Account Name
      { width: 28 }, // Transaction Reference Number
      { width: 12 }, // Return Code
      { width: 15 }, // Credit/Debit Code
      { width: 15 }, // Return Date
      { width: 15 }, // Amount
      { width: 12 }, // Currency Code
      { width: 20 }, // Originating Bank
      { width: 20 }, // Originating Branch
      { width: 20 }, // Originating Account
      { width: 25 }, // Originating Account Name
      { width: 20 }, // Particulars
      { width: 15 }, // Reference
      { width: 12 }, // Value Date
      { width: 15 }, // Security Field
      { width: 10 }, // Filler
      { width: 15 }, // NIC Number
    ];

    const lastColIndex = headers.length - 1;
    worksheet.columns[lastColIndex].width = 16;

    worksheet.properties.outlineProperties = {
      summaryBelow: true,
      summaryRight: true
    };

    worksheet.views = [
      { state: 'frozen', ySplit: 3 }
    ];

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const fileName = `Payroll_Text_${monthFilter || 'current'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
    }).catch((error) => {
      console.error('Error generating Excel file:', error);
      alert('Error generating Excel file. Please try again.');
    });
  };

  // ─── Filter reports based on search ──────────────────────────────────
  const filteredReports = useMemo(() => {
    if (!bankReports || !Array.isArray(bankReports)) return [];

    const q = search.trim().toLowerCase();
    if (!q) return bankReports;

    return bankReports.filter((report) => {
      // Get all string values from the report object
      const searchableFields = Object.values(report).map(value =>
        String(value || '').toLowerCase()
      );

      // Check if any field contains the search term
      return searchableFields.some(field => field.includes(q));
    });
  }, [bankReports, search]);


//   // ── Client-side filter by selected year/month ──
//   // The API (p_Created_Date) does NOT filter by month — confirmed via Postman.

//   const filteredReports = useMemo(() => {
//   if (!bankReports || !Array.isArray(bankReports)) return [];

//   let result = bankReports;

//   if (monthFilter && monthFilter.includes('-')) {
//     const [year, month] = monthFilter.split('-');
//     const targetYear = Number(year);
//     const targetMonth = Number(month);

//     result = result.filter((r) => {
//       const rYear = Number(r.HBR_Year ?? r.year);
//       const rMonth = Number(r.HBR_Month ?? r.month);
//       return rYear === targetYear && rMonth === targetMonth;
//     });
//   }

//   // ── Search filter ──
//   const q = search.trim().toLowerCase();
//   if (q) {
//     result = result.filter((report) =>
//       Object.values(report).some((value) =>
//         String(value || '').toLowerCase().includes(q)
//       )
//     );
//   }

//   return result;
// }, [bankReports, search, monthFilter]);


  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '-';
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ─── Define columns with proper key mappings ──────────────────────────
  const columns = useMemo(() => [
    {
      key: 'HBR_Tran_ID',
      label: 'Transaction ID',
      align: 'center',
      className: 'text-gray-400 tabular-nums',
      render: (row) => row.HBR_Tran_ID || row.id || row.tranId || '0'
    },
    {
      key: 'HBR_Bank_Name',
      label: 'Bank Name',
      className: 'font-semibold text-gray-900',
      render: (row) => row.HBR_Bank_Name || row.bankName || ''
    },
    {
      key: 'HBR_Destination_Bank',
      label: 'Destination Bank',
      className: 'text-gray-600 tabular-nums',
      render: (row) => row.HBR_Destination_Bank || row.destinationBank || ''
    },
    {
      key: 'HBR_Branch_Name',
      label: 'Branch',
      className: 'text-gray-600',
      render: (row) => row.HBR_Branch_Name || row.branchName || ''
    },
    {
      key: 'HBR_Destination_Br',
      label: 'Destination Branch',
      align: 'center',
      className: 'text-gray-600 tabular-nums',
      render: (row) => row.HBR_Destination_Br || row.destinationBr || ''
    },
    {
      key: 'HBR_Branch_Swift_Code',
      label: 'Swift Code',
      className: 'text-gray-600',
      render: (row) => row.HBR_Branch_Swift_Code || row.branchSwiftCode || ''
    },

    {
      key: 'HBR_Year',
      label: 'Year',
      align: 'center',
      className: 'text-gray-700',
      render: (row) => row.HBR_Year ?? row.year ?? '-',
    },
    {
      key: 'HBR_Month',
      label: 'Month',
      align: 'center',
      className: 'text-gray-700',
      render: (row) => {
        const m = row.HBR_Month ?? row.month;
        if (!m) return '-';
        const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return names[Number(m) - 1] || String(m);
      },
    },
    {
      key: 'HBR_Destination_Account',
      label: 'Account',
      className: 'text-gray-700 tabular-nums',
      render: (row) => row.HBR_Destination_Account || row.destinationAccount || ''
    },
    {
      key: 'HBR_Destination_Account_Name',
      label: 'Account Name',
      className: 'text-gray-700',
      render: (row) => row.HBR_Destination_Account_Name || row.destinationAccountName || ''
    },



    {
      key: 'HBR_TRN_Code',
      label: 'Transaction Reference Number',
      align: 'center',
      className: 'text-gray-600 tabular-nums',
      render: (row) => row.HBR_TRN_Code || row.trnCode || '0'
    },
    {
      key: 'HBR_Return_Code',
      label: 'Return',
      align: 'center',
      className: 'text-gray-600 tabular-nums',
      render: (row) => row.HBR_Return_Code || row.returnCode || '0'
    },
    {
      key: 'HBR_Cr_Dr_Code',
      label: 'Credit/Debit',
      align: 'center',
      className: 'text-gray-600 tabular-nums',
      render: (row) => row.HBR_Cr_Dr_Code || row.crDrCode || '0'
    },
    {
      key: 'HBR_Return_Date',
      label: 'Return Date',
      align: 'center',
      className: 'text-gray-600 tabular-nums',
      render: (row) => row.HBR_Return_Date || row.returnDate || '-',
    },
    {
      key: 'HBR_Amount',
      label: 'Amount (LKR)',
      align: 'right',
      className: 'font-semibold text-green-600',
      render: (row) => {
        const amount = row.HBR_Amount || row.amount || '0';
        return formatCurrency(amount);
      },
    },
    {
      key: 'HBR_Amount_Decimal',
      label: 'Amount Decimal',
      align: 'right',
      className: 'font-semibold text-gray-900',
      render: (row) => row.HBR_Amount_Decimal || row.amountDecimal || '0',
    },
    {
      key: 'HBR_Currency_Code',
      label: 'Currency',
      align: 'center',
      className: 'text-gray-600',
      render: (row) => row.HBR_Currency_Code || row.currencyCode || 'SLR'
    },
    {
      key: 'HBR_Originating_Account',
      label: 'Original Account',
      className: 'text-gray-600 tabular-nums',
      render: (row) => row.HBR_Originating_Account || row.originatingAccount || ''
    },

    {
      key: 'HBR_Originating_Account_Name',
      label: 'Original Account Name',
      className: 'text-gray-600',
      render: (row) => row.HBR_Originating_Account_Name || row.originatingAccountName || ''
    },


    {
      key: 'HBR_Particulars',
      label: 'Particulars',
      className: 'text-gray-600',
      render: (row) => row.HBR_Particulars || row.particulars || ''
    },
    {
      key: 'HBR_Reference',
      label: 'Reference',
      className: 'text-gray-600',
      render: (row) => row.HBR_Reference || row.reference || ''
    },
    {
      key: 'HBR_Value_Date',
      label: 'Value Date',
      align: 'center',
      className: 'text-gray-600 tabular-nums',
      render: (row) => row.HBR_Value_Date || row.valueDate || '0'
    },
    {
      key: 'HBR_Security_Field',
      label: 'Security',
      align: 'center',
      className: 'text-gray-600 tabular-nums',
      render: (row) => row.HBR_Security_Field || row.securityField || '0'
    },
    {
      key: 'HBR_Filler',
      label: 'Filler',
      align: 'center',
      className: 'text-gray-400',
      render: (row) => row.HBR_Filler || row.filler || '@'
    },
    {
      key: 'HBR_NIC_Number',
      label: 'NIC',
      className: 'text-gray-600 tabular-nums',
      render: (row) => row.HBR_NIC_Number || row.nicNumber || ''
    },
  ], [formatCurrency]);

  const hasFilter = monthFilter && monthFilter.includes('-');

  // ── TEMPORARY: hard-coded acting stage (replace with real role later) ──
  // Change this ONE line to test 'prepared' | 'authorized' | 'approved'.
  const activeStage = 'prepared';


  return (
    <div className="p-6 space-y-4">
      {/* ── Header ─────────────────────────────────────────────── */}
      <Breadcrumb
        trail={['Payroll', 'Payroll Text']}
        title="Payroll Text File"
        subtitle="Generate and view bank transfer reports by month."
        action={
          <div className="flex gap-2">
            {/* Export Excel Button */}
            <button
              onClick={handleExportExcel}
              disabled={filteredReports.length === 0}
              className={`${BREADCRUMB_ACTION_BTN} bg-green-600 hover:bg-green-700 text-white`}
            >
              <Download size={15} />
              Export
            </button>
            {/* Refresh Button */}
            <button
              onClick={() => {
                if (hasFilter) {
                  const [year, month] = monthFilter.split('-');
                  dispatch(fetchBankReportsByDate(`${year}-${month}`));
                }
              }}
              className={BREADCRUMB_ACTION_BTN}
              disabled={!hasFilter}
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        }
      />

      {/* Error banner */}
      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm flex items-start gap-3">
          <span>{error}</span>
          <button
            onClick={() => dispatch(clearPayrollTextError())}
            className="ml-auto text-amber-500 hover:text-amber-700"
          >
            <span className="sr-only">Close</span>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {validation.stageError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-3">
          <span>{validation.stageError}</span>
          <button
            onClick={() => dispatch(clearStageError())}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Generate Error/Success Message */}
      {generateError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-3">
          <span>{generateError}</span>
          <button
            onClick={() => setGenerateError('')}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <span className="sr-only">Close</span>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {generateSuccess && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-sm flex items-start gap-3">
          <span>Payroll text generated successfully!</span>
          <button
            onClick={() => setGenerateSuccess(false)}
            className="ml-auto text-emerald-500 hover:text-emerald-700"
          >
            <span className="sr-only">Close</span>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Month/Year Filters ────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-3">
        {/* Year and Month each carry their own label. */}
        <MonthYearPicker
          value={monthFilter}
          onChange={setMonthFilter}
          className=""
        />
        <div>
          <span className={LABEL}>Search</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search any field..."
            className={`${FIELD} w-[300px] max-w-full`}
          />
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────── */}
      <Table
        title="Payroll Text Reports"
        meta={`${filteredReports.length} of ${bankReports.length} row${bankReports.length === 1 ? '' : 's'}`}
        columns={columns}
        rows={filteredReports}
        rowKey={(row) =>
          [
            row.HBR_Tran_ID || row.id || row.tranId || '',
            row.year ?? row.HBR_Year ?? '',
            row.month ?? row.HBR_Month ?? '',
            row.reference || row.HBR_Reference || '',
            row.destinationAccount || row.HBR_Destination_Account || '',
          ].join('|')
        }
        loading={loading}
        loadingMessage="Loading bank reports..."
        emptyMessage={
          !hasFilter
            ? 'Please select a month to view reports.'
            : bankReports.length === 0
              ? `No bank reports found for ${monthFilter}.`
              : 'No results match your search criteria.'
        }
        minWidth={2200}
        pageSize={ROWS_PER_PAGE}
        scrollClassName=""
        emptyValue="-"
        defaultSort={{ key: 'HBR_Tran_ID', dir: 'asc' }}
      />

      {/* ── Approve Button ────────────────────────────────────────────── */}
      {!loading && (
        <div className="flex items-center justify-end gap-4 pt-3 border-t border-gray-200">
          <button
            onClick={handleValidate}
            disabled={
              !hasFilter ||
              filteredReports.length === 0 ||
              validation.stageLoading === activeStage ||
              validation[activeStage]
            }
            className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${validation[activeStage]
              ? 'bg-emerald-800 text-white cursor-not-allowed'
              : validation.stageLoading === activeStage
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : filteredReports.length === 0 || !hasFilter
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700 hover:shadow-md'
              }`}
          >
            {validation.stageLoading === activeStage ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Submitting...
              </>
            ) : validation[activeStage] ? (
              <>
                <CheckCircle size={16} />
                {activeStage.charAt(0).toUpperCase() + activeStage.slice(1)}
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                Mark as {activeStage.charAt(0).toUpperCase() + activeStage.slice(1)}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default PayrollText;